import React, { useState, useEffect, useRef } from 'react'
import { InputDiv, Label, Input, SearchOptions, Option } from '../../styles/select-style'
import ApiService from '../../../services/ApiService'
import { CertificateType } from '../../../pages/ClientPanel/Certificates/CreateCertificate'
import { mergeCertificateWithServiceDetail } from './opFromService'

type ServiceOption = {
  id: number
  codeOfOP?: string | null
  OrderItem?: {
    Order?: {
      Company_Order_clientIdToCompany?: { companyName?: string | null }
    }
  }
}

type PropsType = {
  certificate: CertificateType
  setCertificate: React.Dispatch<React.SetStateAction<CertificateType>>
  itsOurs: boolean
}

const MIN_OP_SEARCH_LENGTH = 3

const SelectProductionOrderInCertificate: React.FC<PropsType> = ({
  certificate,
  setCertificate,
  itsOurs,
}) => {
  const [items, setItems] = useState<ServiceOption[]>([])
  const [inputOp, setInputOp] = useState('')
  const [showOptions, setShowOptions] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const optionsRef = useRef<HTMLUListElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    setInputOp(
      certificate.codeOpString?.trim()
        ? certificate.codeOpString
        : certificate.serviceId
          ? `OP #${certificate.serviceId}`
          : '',
    )
  }, [certificate.serviceId, certificate.codeOpString])

  useEffect(() => {
    if (!showOptions) return
    setSelectedIndex(0)
    const q = inputOp.trim()
    if (q.length < MIN_OP_SEARCH_LENGTH) {
      setItems([])
      return
    }
    const handle = setTimeout(() => {
      ApiService.productionFlow
        .listServices({
          search: q,
          quantity: 40,
        })
        .then((response: any) => {
          const data = response.data
          setItems(Array.isArray(data) ? data : [])
        })
        .catch(() => setItems([]))
    }, 300)
    return () => clearTimeout(handle)
  }, [inputOp, showOptions])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(t) &&
        !inputRef.current.contains(t)
      ) {
        setShowOptions(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const pick = (op: ServiceOption) => {
    setShowOptions(false)
    ApiService.productionFlow.getServiceById(op.id).then((response: any) => {
      if (response.data) {
        setCertificate((prev) =>
          mergeCertificateWithServiceDetail(prev, response.data),
        )
      }
    })
  }

  return (
    <InputDiv>
      <Label>Ordem de produção</Label>
      <Input
        ref={inputRef}
        type='text'
        placeholder={
          itsOurs ? `Buscar OP… (mín. ${MIN_OP_SEARCH_LENGTH} caracteres)` : 'Selecione'
        }
        value={inputOp}
        onFocus={() => setShowOptions(true)}
        onChange={(e) => setInputOp(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && selectedIndex < items.length - 1) {
            setSelectedIndex((i) => i + 1)
          }
          if (e.key === 'ArrowUp' && selectedIndex > 0) {
            setSelectedIndex((i) => i - 1)
          }
          if (e.key === 'Enter' && items[selectedIndex]) pick(items[selectedIndex])
          if (e.key === 'Tab') setShowOptions(false)
        }}
        disabled={!itsOurs}
      />
      {showOptions && itsOurs && (
        <SearchOptions ref={optionsRef}>
          {inputOp.trim().length < MIN_OP_SEARCH_LENGTH ? (
            <li
              style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                color: '#666',
                cursor: 'default',
              }}
            >
              Digite pelo menos {MIN_OP_SEARCH_LENGTH} caracteres para buscar
            </li>
          ) : (
            items.map((op, index) => {
              const client =
                op.OrderItem?.Order?.Company_Order_clientIdToCompany?.companyName
              return (
                <Option
                  key={op.id}
                  onClick={() => pick(op)}
                  isSelected={index === selectedIndex}
                  style={{ alignItems: 'flex-start' }}
                >
                  <span style={{ fontWeight: 700 }}>
                    {op.codeOfOP?.trim() || `OP #${op.id}`}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#666' }}>
                    {client || `Id ${op.id}`}
                  </span>
                </Option>
              )
            })
          )}
        </SearchOptions>
      )}
    </InputDiv>
  )
}

export default SelectProductionOrderInCertificate
